import { initSocket } from './socket.js';
import { applyIdentity, initLobbyUI } from './lobbyUI.js';
import { initBoardRenderer, updateBoard } from './boardRenderer.js';
import { initActionPanel, updateActions } from './actionPanel.js';
import { initUIPanel, updateUI } from './uiPanel.js';
import { initCardModal, checkCardDrawn } from './cardModal.js';
import { initTradeModal, checkTradeIncoming } from './tradeModal.js';
import { initChatUI } from './chatUI.js';
import { applyState, getLatestState, setStateHandler } from './state.js';
import { GameState, GamePhase } from '../shared/types.js';

// Replaced by build.mjs at bundle time, so the stamp on screen always
// matches the deployed bundle rather than a hand-edited string.
declare const __BUILD__: string;
for (const el of document.querySelectorAll('.build-stamp')) {
  el.textContent = `build ${__BUILD__}`;
}

/**
 * Publish the shared auth bar's height as `--auth-bar-h`.
 *
 * The bar is injected by the hub's auth script and is fixed to the top of the
 * viewport, so it is not part of this page's layout. The game screen is a full
 * 100vh, which put the board and the room controls underneath it. Its height
 * changes with sign-in state (a guest gets extra lines of explanation) and with
 * wrapping on narrow screens, so measure it rather than assuming a number.
 */
function trackAuthBarHeight(): void {
  const setVar = (height: number): void => {
    document.documentElement.style.setProperty('--auth-bar-h', `${height}px`);
  };
  const attach = (): boolean => {
    const bar = document.getElementById('auth-bar');
    if (!bar) return false;
    setVar(bar.getBoundingClientRect().height);
    new ResizeObserver(() => setVar(bar.getBoundingClientRect().height)).observe(bar);
    return true;
  };
  if (attach()) return;
  // The auth script is a separate module and may not have run yet.
  const pending = new MutationObserver(() => {
    if (attach()) pending.disconnect();
  });
  pending.observe(document.body, { childList: true });
}
trackAuthBarHeight();

// Who we are in GameState. This comes from the account behind the JWT, so
// it is the same before and after a reconnect — unlike socket.id, which
// changes and used to leave us unable to recognise our own turn.
let myPlayerId = '';

const socket = initSocket({
  onIdentity: (data) => {
    applyIdentity(data);
    const id = data.playerId ?? '';
    if (!id || id === myPlayerId) return;
    myPlayerId = id;
    const latest = getLatestState();
    if (latest) renderState(latest);
  },
});

initLobbyUI(socket);
initBoardRenderer();
initActionPanel();
initUIPanel();
initCardModal();
initTradeModal(socket);
initChatUI(socket);

let gameStarted = false;

function renderState(state: GameState): void {
  if (!gameStarted) {
    gameStarted = true;
    showScreen('screen-game');
    const roomCodeEl = document.getElementById('game-room-code');
    if (roomCodeEl) roomCodeEl.textContent = state.roomCode;
    // The admin button visibility is driven by the 'identity' event from the
    // server (handled in lobbyUI.ts), so nothing extra to do here.
  }

  updateBoard(state);
  updateUI(state, myPlayerId);
  updateActions(state, myPlayerId);
  checkCardDrawn(state);
  checkTradeIncoming(state, myPlayerId);

  if (state.phase === GamePhase.GAME_OVER) {
    const winner = state.players.find(p => p.id === state.winner);
    const winnerText = document.getElementById('winner-text');
    if (winnerText) {
      winnerText.textContent = winner
        ? `${winner.name} wins the game!`
        : 'Game Over!';
    }
    showScreen('screen-gameover');
  }
}

setStateHandler(renderState);
socket.on('game:state', (state: GameState) => {
  applyState(state);
});

export function showScreen(screenId: string): void {
  document.querySelectorAll('.screen').forEach(el => {
    el.classList.remove('active');
    el.classList.add('hidden');
  });
  const screen = document.getElementById(screenId);
  if (screen) {
    screen.classList.remove('hidden');
    screen.classList.add('active');
  }
  if (screenId === 'screen-game') {
    requestAnimationFrame(scaleBoard);
  }
}

// New game / Exit game buttons
const newGameBtn = document.getElementById('btn-new-game');
if (newGameBtn) {
  newGameBtn.addEventListener('click', () => {
    window.location.reload();
  });
}

const exitGameBtn = document.getElementById('btn-exit-game');
if (exitGameBtn) {
  exitGameBtn.addEventListener('click', () => {
    window.location.reload();
  });
}

// Help modal
const helpBtn = document.getElementById('btn-help');
const helpModal = document.getElementById('help-modal');
const helpClose = document.getElementById('help-close');

if (helpBtn && helpModal) {
  helpBtn.addEventListener('click', () => {
    // Reset to first tab
    document.querySelectorAll('.help-tab').forEach((t, i) => {
      t.classList.toggle('active', i === 0);
    });
    document.querySelectorAll('.help-tab-panel').forEach((p, i) => {
      p.classList.toggle('hidden', i !== 0);
    });
    helpModal.classList.remove('hidden');
  });
}

if (helpClose && helpModal) {
  helpClose.addEventListener('click', () => helpModal.classList.add('hidden'));
}

document.querySelectorAll('.help-tab').forEach(btn => {
  btn.addEventListener('click', () => {
    const tab = (btn as HTMLElement).dataset.tab;
    document.querySelectorAll('.help-tab').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    document.querySelectorAll('.help-tab-panel').forEach(p => p.classList.add('hidden'));
    const panel = document.getElementById(`help-tab-${tab}`);
    if (panel) panel.classList.remove('hidden');
  });
});

// Feedback modal
const feedbackBtn = document.getElementById('btn-feedback');
const feedbackModal = document.getElementById('feedback-modal');
const feedbackCancel = document.getElementById('feedback-cancel');
const feedbackSubmit = document.getElementById('feedback-submit');
const feedbackText = document.getElementById('feedback-text') as HTMLTextAreaElement;
const feedbackCharCount = document.getElementById('feedback-char-count');

if (feedbackBtn && feedbackModal) {
  feedbackBtn.addEventListener('click', () => {
    if (feedbackText) feedbackText.value = '';
    if (feedbackCharCount) feedbackCharCount.textContent = '0 / 1000';
    feedbackModal.classList.remove('hidden');
  });
}

if (feedbackText && feedbackCharCount) {
  feedbackText.addEventListener('input', () => {
    feedbackCharCount.textContent = `${feedbackText.value.length} / 1000`;
  });
}

if (feedbackCancel && feedbackModal) {
  feedbackCancel.addEventListener('click', () => {
    feedbackModal.classList.add('hidden');
  });
}

if (feedbackSubmit && feedbackModal && feedbackText) {
  feedbackSubmit.addEventListener('click', () => {
    const typeEl = document.querySelector('input[name="feedback-type"]:checked') as HTMLInputElement;
    const type = (typeEl?.value === 'suggestion' ? 'suggestion' : 'bug') as 'bug' | 'suggestion';
    const text = feedbackText.value.trim();
    if (!text) {
      alert('Please enter some text before submitting.');
      return;
    }
    socket.emit('feedback:submit', type, text, (response: any) => {
      if ('error' in response) {
        alert(response.error);
      } else {
        feedbackModal.classList.add('hidden');
        alert('Thank you for your feedback!');
      }
    });
  });
}

// ─── Board scaling ──────────────────────────────────────────────────────────
// The board grid is 100 + 9×70 + 100 = 830px plus 3px border each side = 836px.
// Scale it down (never up) so it always fits within the viewport.
function scaleBoard(): void {
  const container = document.getElementById('board-container');
  if (!container) return;
  const natural = 836;
  // Available width: viewport minus side-panel (300) + gap (20) + padding (40)
  const availW = window.innerWidth - 360;
  // Available height: viewport minus vertical padding (40)
  const availH = window.innerHeight - 40;
  const scale = Math.min(Math.max(availW, 0) / natural, Math.max(availH, 0) / natural, 1);
  container.style.transformOrigin = 'top left';
  container.style.transform = `scale(${scale})`;
  // Collapse empty space with negative margins so side-panel stays adjacent
  const shrink = natural * (1 - scale);
  container.style.marginRight = `-${shrink}px`;
  container.style.marginBottom = `-${shrink}px`;
}
window.addEventListener('resize', scaleBoard);
